import { Fragment, useState } from "react";

import { Column, Text, Row, FONT_SIZES, TextButton, Image, Tooltip, Icon, TextVariant } from "@bridgesplit/ui";
import { BsMetaUtil, LoopExpandedOrca } from "@bridgesplit/abf-react";
import { calculatePercentChange, formatNum, formatPercent, Result, roundToDecimals } from "@bridgesplit/utils";
import { SwapHorizOutlined } from "@mui/icons-material";
import { Divider } from "@mui/material";
import { tickIndexToPrice } from "@orca-so/whirlpool-sdk";
import { isMaxTickIndex, isMinTickIndex } from "app/components/loan/actions/whirlpool/util";
import { OrcaRouteParams } from "@bridgesplit/abf-sdk";

export function WindWhirlpoolStats({
    loopExpanded,
    variant
}: {
    loopExpanded: LoopExpandedOrca;
    variant: TextVariant;
}) {
    //Add OrcaRouteParams to Pandora
    const quoteFetchParams = loopExpanded.loopVault.quoteFetchParams as unknown as OrcaRouteParams;

    const whirlpool = loopExpanded.whirlpool;
    const whirlpoolOffchain = loopExpanded.whirlpoolOffchain;

    const [flippedPrice, setFlippedPrice] = useState(false);

    // token images in correct order
    const tokens = flippedPrice ? [whirlpool.tokenB, whirlpool.tokenA] : [whirlpool.tokenA, whirlpool.tokenB];

    if (whirlpoolOffchain) {
        const upperPrice = getPriceFromTickFlipped(
            quoteFetchParams.tickUpper,
            whirlpoolOffchain.tickSpacing,
            whirlpool.tokenA?.decimals,
            whirlpool.tokenB?.decimals,
            flippedPrice
        );
        const lowerPrice = getPriceFromTickFlipped(
            quoteFetchParams.tickLower,
            whirlpoolOffchain.tickSpacing,
            whirlpool.tokenA?.decimals,
            whirlpool.tokenB?.decimals,
            flippedPrice
        );
        const lowerPercent = getWhirlpoolPercentChangeFromPrices({
            price: lowerPrice,
            currentPrice: whirlpoolOffchain.price,
            flipped: flippedPrice
        });

        const upperPercent = getWhirlpoolPercentChangeFromPrices({
            price: upperPrice,
            currentPrice: whirlpoolOffchain.price,
            flipped: flippedPrice
        });

        return (
            <Row spacing={1} alignItems="center">
                {/* Prices + % changes */}
                <Tooltip
                    minTooltipWidth="175px"
                    title={<PercentTooltipTitle lowerPercent={lowerPercent} upperPercent={upperPercent} />}
                    reverseColors
                >
                    <TextButton variant="body2" color="disabled">
                        <Icon type="help" />
                    </TextButton>
                </Tooltip>

                <Text variant={variant}>
                    {`${formatNum(lowerPrice, { customDecimals: PRICE_RANGE_DECIMALS })} / ${formatNum(upperPrice, {
                        customDecimals: PRICE_RANGE_DECIMALS
                    })} `}
                </Text>

                <Row spacing={0.5}>
                    {tokens.map((token, i) => (
                        <Fragment key={`${token?.mint}`}>
                            {i !== 0 && <Text color="disabled">/</Text>}
                            <Image
                                size={`${FONT_SIZES[variant]}px`}
                                variant="circle"
                                src={BsMetaUtil.getImage(token)}
                            />
                        </Fragment>
                    ))}
                </Row>

                <TextButton variant="body2" onClick={() => setFlippedPrice((prev) => !prev)} color="disabled">
                    <SwapHorizOutlined />
                </TextButton>
            </Row>
        );
    }

    return null;
}

function PercentTooltipTitle({
    lowerPercent,
    upperPercent
}: {
    lowerPercent: string | undefined;
    upperPercent: string | undefined;
}) {
    if (!lowerPercent || !upperPercent) return null;
    return (
        <Column spacing={1} sx={{ padding: 1 }}>
            <Text variant="body2" color="caption">
                Min: {lowerPercent}
            </Text>
            <Divider />
            <Text variant="body2" color="caption">
                Max: {upperPercent}
            </Text>
        </Column>
    );
}

export type FormatPercentChangeParams = {
    tickIndex: number;
    currentTickIndex: number;
    tickSpacing: number;
    tokenADecimals: number;
    tokenBDecimals: number;
    flipped: boolean;
};

export function getWhirlpoolPercentChangeFromPrices({
    price,
    currentPrice,
    flipped
}: {
    price: number | undefined;
    currentPrice: string | undefined;
    flipped: boolean;
}): string | undefined {
    if (price === undefined || currentPrice === undefined) return undefined;

    const parsedCurrentPrice = parseFloat(currentPrice);
    if (isNaN(parsedCurrentPrice)) return undefined;
    const currentPriceFlipped = flipPriceIfNeeded(parsedCurrentPrice, flipped);
    if (currentPriceFlipped === undefined) return undefined;

    let percentChange = calculatePercentChange(currentPriceFlipped, price);
    if (flipped) {
        percentChange = -percentChange;
    }

    let formatted = formatPercent(percentChange);
    if (percentChange > 0) formatted = `+${formatted}`;
    return formatted;
}

const PRICE_RANGE_DECIMALS = 6;
function getPriceFromTickFlipped(
    tickIndex: number | undefined,
    tickSpacing: number | undefined,
    tokenADecimals: number | undefined,
    tokenBDecimals: number | undefined,
    isFlippedPrice: boolean | undefined
): number | undefined {
    if (
        tickIndex === undefined ||
        tickSpacing === undefined ||
        tokenADecimals === undefined ||
        tokenBDecimals === undefined ||
        isFlippedPrice === undefined
    ) {
        return undefined;
    }

    const price = getPriceFromTick({ tickIndex, tickSpacing, tokenADecimals, tokenBDecimals });
    const flippedPrice = flipPriceIfNeeded(price, isFlippedPrice);
    return flippedPrice;
}

function flipPriceIfNeeded(price: number | undefined, isFlippedPrice: boolean) {
    if (!price) return price;
    return isFlippedPrice ? roundToDecimals(1 / price, PRICE_RANGE_DECIMALS) : price;
}

export type GetPriceFromTickParams = {
    tickIndex: number;
    tickSpacing: number;
    tokenADecimals: number;
    tokenBDecimals: number;
};

// Re-using this function from the loan/actions/whirlpool/util.ts file, it would be a good idea to move to shared orca utils folder
function getPriceFromTick({
    tickIndex,
    tickSpacing,
    tokenADecimals,
    tokenBDecimals
}: GetPriceFromTickParams): number | undefined {
    try {
        if (isMaxTickIndex(tickIndex, tickSpacing)) return Infinity;
        if (isMinTickIndex(tickIndex, tickSpacing)) return 0;
        return tickIndexToPrice(tickIndex, tokenADecimals, tokenBDecimals).toNumber();
    } catch (error) {
        Result.err(error);
        return undefined;
    }
}
